import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowSquareOut, Copy, EnvelopeSimple, PencilSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { AdminModal, BTN_BLACK, BTN_WHITE, INPUT, errText } from "@/components/pageSettings/AdminModal";
import { verticalLabel } from "@/lib/verticalPresets";

const STYLE = { quiz: "Quiz", focused_funnel: "Focused Funnel" };
const styleOf = (p) => STYLE[(p.form_experience || "").toLowerCase()] || "Standard";

function LinkRow({ label, url, testId }) {
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.error("Couldn't copy"));
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <div className="flex-1 font-mono text-[11px] break-all border border-black bg-white px-2 py-1.5" data-testid={`${testId}-url`}>{url}</div>
        <button type="button" onClick={copy} title="Copy" data-testid={`${testId}-copy`} className="border border-black px-2 py-1.5 hover:bg-black hover:text-white"><Copy size={12} weight="bold" /></button>
        <a href={url} target="_blank" rel="noreferrer" title="Open" data-testid={`${testId}-open`} className="border border-black px-2 py-1.5 hover:bg-black hover:text-white"><ArrowSquareOut size={12} weight="bold" /></a>
      </div>
    </div>
  );
}

function Result({ r, firm }) {
  const subject = `Your landing page preview${firm ? ` — ${firm}` : ""}`;
  const body = [
    `Hi${firm ? ` ${firm} team` : " there"},`, "",
    "Here's what your landing page and thank-you page could look like:", "",
    `Landing page: ${r.lp}`, `Thank-you page: ${r.ty}`, "",
    `These preview links work for ${r.days} days. The form is for show only, so nothing typed into it is sent anywhere.`, "",
    "— [Your name]",
  ].join("\n");
  return (
    <div className="border-2 border-emerald-700 bg-emerald-50 p-4 space-y-3" data-testid="prospect-result">
      <div className="text-xs font-bold">
        {r.page ? `Draft page /lp/${r.page.slug} made for ${firm}. It isn't live and can't take leads.` : `Branded preview links ready${firm ? ` for ${firm}` : ""}.`}
        <span className="font-normal"> The links work for {r.days} days; the form is blocked so no lead is made.</span>
      </div>
      <LinkRow label="Landing page" url={r.lp} testId="prospect-lp" />
      <LinkRow label="Thank-you page" url={r.ty} testId="prospect-ty" />
      <div className="flex flex-wrap gap-2">
        <a href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`} className={BTN_BLACK} data-testid="prospect-email">
          <EnvelopeSimple size={11} weight="bold" /> Email these links
        </a>
        {r.page && (
          <a href={`/admin/landing-pages/custom/${r.page.id}`} className={BTN_WHITE} data-testid="prospect-edit-draft">
            <PencilSimple size={11} weight="bold" /> Edit the draft
          </a>
        )}
      </div>
    </div>
  );
}

// Show a prospect what their landing + thank-you page could look like: branded sample links, or a draft page made for them.
export function ProspectPreviewModal({ samples, origin, onClose, onDraftCreated }) {
  const [firm, setFirm] = useState("");
  const [phone, setPhone] = useState("");
  const [pick, setPick] = useState(samples[0]?.id || "");
  const [days, setDays] = useState(14);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const f = firm.trim();
  const p = phone.trim();

  const mint = async (page) => (await api.post(`/admin/custom-landing-pages/${page.id}/preview-tokens`,
    { ttl_days: days, label: `Prospect: ${f || "unnamed"}`.slice(0, 120) })).data.token;
  const links = (page, token, brand) => {
    const lp = new URLSearchParams({ demo: token });
    const ty = new URLSearchParams({ slug: page.slug, demo: token });
    if (brand.firm) { lp.set("firm", brand.firm); ty.set("firm", brand.firm); }
    if (brand.phone) { lp.set("phone", brand.phone); ty.set("phone", brand.phone); }
    if ((page.form_experience || "") === "focused_funnel") ty.set("experience", "focused_funnel");
    return { lp: `${origin}/lp/${page.slug}?${lp}`, ty: `${origin}/lp/thank-you?${ty}`, days };
  };
  const run = async (kind) => {
    setBusy(kind);
    try {
      const sample = samples.find((s) => s.id === pick);
      if (kind === "links") {
        setResult(links(sample, await mint(sample), { firm: f, phone: p }));
      } else {
        const { data: page } = await api.post(`/admin/custom-landing-pages/${pick}/prospect-draft`, { firm_name: f, phone: p || null });
        setResult({ ...links(page, await mint(page), { firm: page.firm_name, phone: page.phone }), page });
        onDraftCreated?.();
      }
    } catch (e) {
      toast.error(errText(e, kind === "links" ? "Couldn't make the links" : "Couldn't make the draft page"));
    } finally {
      setBusy("");
    }
  };

  return (
    <AdminModal eyebrow="Show a prospect" title="Their landing page and thank-you page" onClose={onClose} testId="prospect-modal">
      <div className="space-y-4">
        <p className="text-[11px] text-zinc-600 leading-relaxed">
          Pick a sample page and type the prospect&apos;s firm name and phone. <b>Branded links</b> show the sample with their
          name (and phone, unless phone display is turned off). <b>Make a draft page</b> also creates a real draft page for
          them that you can edit before or after you send it. Either way, their form never creates a lead.
        </p>
        {!samples.length ? (
          <p className="text-xs" data-testid="prospect-no-samples">No sample pages yet. A super admin can press Restore samples to add them.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 text-xs">
            <label className="sm:col-span-2"><span className="label-mono">Sample page</span>
              <select className={INPUT} value={pick} onChange={(e) => setPick(e.target.value)} data-testid="prospect-sample">
                {samples.map((s) => <option key={s.id} value={s.id}>{verticalLabel(s.vertical || "other")} · {styleOf(s)} — {s.firm_name || s.slug}</option>)}
              </select>
            </label>
            <label><span className="label-mono">Prospect&apos;s firm name</span>
              <input className={INPUT} value={firm} onChange={(e) => setFirm(e.target.value)} maxLength={120} placeholder="e.g. Smith Injury Law" data-testid="prospect-firm" />
            </label>
            <label><span className="label-mono">Their phone</span>
              <input className={INPUT} value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} placeholder="(555) 555-0100" data-testid="prospect-phone" />
            </label>
            <label><span className="label-mono">Links work for</span>
              <select className={INPUT} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="prospect-days">
                {[7, 14, 30, 60].map((d) => <option key={d} value={d}>{d} days</option>)}
              </select>
            </label>
            <div className="flex flex-wrap items-end gap-2">
              <button type="button" className={BTN_BLACK} disabled={!!busy || !pick} onClick={() => run("links")} data-testid="prospect-make-links">
                {busy === "links" ? "Making…" : "Branded links"}
              </button>
              <button type="button" className={BTN_WHITE} disabled={!!busy || !pick || !f} onClick={() => run("draft")} data-testid="prospect-make-draft"
                title={f ? "" : "Type their firm name first"}>
                {busy === "draft" ? "Making…" : "Make a draft page"}
              </button>
            </div>
          </div>
        )}
        {result && <Result r={result} firm={result.page?.firm_name || f} />}
      </div>
    </AdminModal>
  );
}
